import { NavLink, Outlet } from 'react-router-dom';
import { User, Shield, Users, Building, Gauge, CreditCard, KeyRound, Palette, Webhook, Terminal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK, type UserRole } from '@praxara/shared/constants';

const NAV: Array<{ to: string; label: string; icon: React.ComponentType<{ className?: string }>; minRole?: UserRole }> = [
  { to: '/settings/profile', label: 'Profile', icon: User },
  { to: '/settings/security', label: 'Security', icon: Shield },
  { to: '/settings/team', label: 'Team', icon: Users, minRole: 'ADMIN' },
  { to: '/settings/tenant', label: 'Organisation', icon: Building, minRole: 'ADMIN' },
  { to: '/settings/pack-template', label: 'Pack branding', icon: Palette, minRole: 'ADMIN' },
  { to: '/settings/usage', label: 'Usage', icon: Gauge, minRole: 'MANAGER' },
  { to: '/settings/billing', label: 'Billing', icon: CreditCard, minRole: 'ADMIN' },
  { to: '/settings/sso', label: 'SSO (SAML)', icon: KeyRound, minRole: 'ADMIN' },
  { to: '/settings/webhooks', label: 'Webhooks', icon: Webhook, minRole: 'ADMIN' },
  { to: '/settings/api-keys', label: 'API keys', icon: Terminal, minRole: 'ADMIN' },
];

export function SettingsLayout() {
  const user = useAuthStore((s) => s.user);
  const visible = NAV.filter((n) => !n.minRole || (user && ROLE_RANK[user.role] >= ROLE_RANK[n.minRole]));

  return (
    <div className="grid gap-8 md:grid-cols-[220px,1fr]">
      <aside>
        <h2 className="font-display text-xs uppercase tracking-wider text-muted-foreground mb-3 px-2">Settings</h2>
        <nav className="space-y-0.5">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <main className="min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
